/**
 * Enter 로 보내기 — **한글 조합 중에도 한 번에** (2026-08-17).
 *
 * ## 무엇이 문제였나
 *
 * 입력창은 다들 이렇게 썼다:
 *
 * ```ts
 * if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) { send(); }
 * ```
 *
 * 그런데 한글을 치다가 Enter 를 누르면 그 Enter 는 **조합을 확정하는 키**다 — keydown 에서
 * `isComposing` 이 참이라 위 조건이 걸러 낸다. 그래서 마지막 글자가 조합 중이면(=한글 문장
 * 끝에서 늘 그렇다) **첫 Enter 는 삼켜지고 두 번 눌러야** 보내진다. 영문만 치면 안 겪는다.
 *
 * ## 어떻게 고치나
 *
 * 조합 중 Enter 를 **무시하지 않고 기억해 둔다**. 브라우저는 `keydown(조합중) →
 * compositionend → input → keyup` 순으로 주므로, **keyup 때 보낸다** — 그때는 조합이 끝났고
 * 입력 상태도 마지막 글자까지 반영돼 있다. 같은 **한 번의 Enter** 로 보내지는 셈이다.
 *
 * ⚠ 조합 중 keydown 에서는 `preventDefault()` 를 **하지 않는다**. 그 키는 IME 가 확정에 쓰는
 *   키라, 막으면 글자가 안 들어가는 브라우저가 있다.
 */

import { useRef, type KeyboardEvent } from 'react';

export interface EnterToSendHandlers<T extends HTMLElement> {
  onKeyDown(e: KeyboardEvent<T>): void;
  onKeyUp(e: KeyboardEvent<T>): void;
}

/**
 * @param send 보낼 때 부를 것. Shift+Enter 는 줄바꿈이라 부르지 않는다.
 * @returns textarea 에 그대로 펼쳐 넣을 두 핸들러.
 */
export function useEnterToSend<T extends HTMLElement = HTMLTextAreaElement>(
  send: () => void,
): EnterToSendHandlers<T> {
  /** 조합 중에 눌린 Enter 를 기억해 둔다 — keyup 에서 갚는다. */
  const pending = useRef(false);

  return {
    onKeyDown(e: KeyboardEvent<T>) {
      if (e.key !== 'Enter' || e.shiftKey) return;
      if (e.nativeEvent.isComposing) {
        // 조합을 확정하는 Enter — 여기서 보내면 마지막 글자가 빠진다. 기억만 한다.
        pending.current = true;
        return;
      }
      pending.current = false;
      e.preventDefault();
      send();
    },
    onKeyUp(e: KeyboardEvent<T>) {
      if (e.key !== 'Enter' || e.shiftKey || !pending.current) return;
      pending.current = false;
      // 이 시점에는 compositionend·input 이 지나가 입력 상태가 최신이다.
      e.preventDefault();
      send();
    },
  };
}
